import React, { useState, useEffect } from "react";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Button, Card, Col, Container, Form, Modal, Row, Image } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import OtpInput from "react-otp-input";
import ProfileImage from '../../images/sampleProfile.png';
import IocnCamera from '../../images/icon-camera.png';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import apiConfig from "../credentials/apiConfig"; // Make sure path is correct
import Loader from "../../components/Loader";

const dummyOtp = "12345";

const EditProfile = () => {
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otp, setOtp] = useState("");
    const [tempEmail, setTempEmail] = useState("");
    const [verifiedEmail, setVerifiedEmail] = useState("");
    const [emailVerificationError, setEmailVerificationError] = useState(false);
    const [isEmailVerified, setIsEmailVerified] = useState(true);
    const [profileImage, setProfileImage] = useState(null);
    const [previewImage, setPreviewImage] = useState('');
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        companyName: "",
        title: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        stateCode: "", // Added stateCode field
        zip: ""
    });
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [states, setStates] = useState([]);
    const [statesLoading, setStatesLoading] = useState(false);
    const [statesError, setStatesError] = useState(null);

    const navigate = useNavigate();

    // Fetch states data
    useEffect(() => {
        const fetchStates = async () => {
            setStatesLoading(true);
            setStatesError(null);
            try {
                const response = await axios.get(
                    `${apiConfig.apiBaseURL}/getAllStates`,
                    {
                        headers: {
                            "x-api-key": apiConfig["x-api-key"],
                            "Content-Type": "application/json",
                        },
                    }
                );
                if (response.data && response.data.success) {
                    const usStates = response.data.data.filter(
                        (state) => state.countryId === 231 && !state.deleted
                    );

                    usStates.sort((a, b) => a.name.localeCompare(b.name));
                    setStates(usStates);
                } else {
                    setStatesError("Failed to fetch states data");
                }
            } catch (error) {
                console.error("Error fetching states:", error);
                setStatesError(
                    "Error fetching states: " + (error.message || "Unknown error")
                );
            } finally {
                setStatesLoading(false);
            }
        };

        fetchStates();
    }, []);

    // Fetch user data when component mounts
    useEffect(() => {
        const fetchUserData = async () => {
            const userId = localStorage.getItem("userId");

            // Check if user is already authenticated
            if (userId) {
                try {
                    // Fetch user profile data from API
                    const response = await axios.get(
                        `${apiConfig.apiBaseURL}/getUserProfile/${userId}`,
                        {
                            headers: {
                                "x-api-key": apiConfig["x-api-key"],
                                "Content-Type": "application/json"
                            }
                        }
                    );

                    const data = response.data;
                    if (data && data.success && data.user) {
                        //console.log("API Response data:", data.user);

                        // Prepare user data for form
                        const userData = {
                            firstName: data.user.firstName || '',
                            lastName: data.user.lastName || '',
                            email: data.user.email || '',
                            phone: formatPhoneNumber(data.user.phoneNumber) || '',
                            companyName: data.user.companyName || '',
                            title: data.user.title || '',
                            address1: data.user.address1 || '',
                            address2: data.user.address2 || '',
                            city: data.user.city || '',
                            state: data.user.state || '',
                            stateCode: data.user.stateCode || '',
                            zip: data.user.zipCode || ''
                        };

                        // Update states
                        setFormData(userData);
                        setVerifiedEmail(data.user.email || '');
                        setPreviewImage(data.user.profileImageUrl);
                        setLoading(false);
                    } else {
                        console.error("Failed to load profile data from API");
                        // fallbackToLocalStorage();
                    }
                } catch (error) {
                    console.error("Error fetching user profile from API:", error);
                }
            } else {
                // User is not logged in, redirect to login
                navigate("/");
            }
        };

        fetchUserData();
    }, [navigate]);

    // Format phone number to (XXX) XXX-XXXX
    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return '';

        // Remove all non-numeric characters
        const numericValue = phoneNumber.replace(/\D/g, '');

        // Format based on length
        if (numericValue.length === 0) return '';
        if (numericValue.length <= 3) return `(${numericValue}`;
        if (numericValue.length <= 6) return `(${numericValue.slice(0, 3)}) ${numericValue.slice(3)}`;
        return `(${numericValue.slice(0, 3)}) ${numericValue.slice(3, 6)}-${numericValue.slice(6, 10)}`;
    };

    // Extract only numbers from formatted phone number
    const extractPhoneDigits = (formattedNumber) => {
        return formattedNumber ? formattedNumber.replace(/\D/g, '') : '';
    };

    const validationSchema = Yup.object({
        firstName: Yup.string().required("First Name is required"),
        lastName: Yup.string().required("Last Name is required"),
        email: Yup.string().email("Invalid email").required("Email is required"),
        phone: Yup.string()
            .test(
                'is-valid-phone',
                'Invalid phone number',
                function (value) {
                    if (!value) return true; // Allow empty
                    const digits = value.replace(/\D/g, "");
                    return digits.length === 10;
                }
            ),
        companyName: Yup.string().required("Company Name is required"),
        title: Yup.string(),
        address1: Yup.string().required("Address 1 is required"),
        address2: Yup.string(),
        city: Yup.string().required("City is required"),
        state: Yup.string().required("State is required"),
        zip: Yup.string()
            .matches(/^[0-9]{5}$/, "Zip Code must be exactly 5 digits")
            .required("Zip is required"),
    });

    const handleOtpChange = (value) => {
        setOtp(value);
    };

    const handleVerifyEmail = () => {
        setShowOtpModal(true);
    };

    const handleOtpSubmit = () => {
        if (otp === dummyOtp) {
            setVerifiedEmail(tempEmail);
            setEmailVerificationError(false);
            setIsEmailVerified(true);
            toast.success("Email verified successfully!", { icon: "✅" });
            setShowOtpModal(false);
        } else {
            toast.error("Invalid OTP. Please try again.");
        }
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const newImageUrl = URL.createObjectURL(file);

            // Check if the new file is different from the existing one
            if (previewImage !== newImageUrl) {
                setProfileImage(file);
                setPreviewImage(newImageUrl);
            } else {
                setProfileImage(null); // Reset if the image is the same
            }
        }
    };

    const handleCancel = () => {
        // toast.warn("No changes were made!");
        navigate("/profile");
    };

    const handleSubmit = async (values) => {
        if (values.email !== verifiedEmail) {
            setEmailVerificationError(true);
            toast.error("Please verify your email before updating your profile.");
            return;
        }

        setIsSubmitting(true);

        try {
            const userId = localStorage.getItem("userId");

            // Create FormData for consistency with Profile component
            const formData = new FormData();

            // Extract just the digits from the phone number
            const phoneDigits = extractPhoneDigits(values.phone);

            // Append all form fields to FormData
            formData.append("id", userId);
            formData.append("firstName", values.firstName);
            formData.append("lastName", values.lastName);
            formData.append("email", values.email);
            formData.append("phoneNumber", phoneDigits);
            formData.append("companyName", values.companyName);
            formData.append("title", values.title);
            formData.append("address1", values.address1);
            formData.append("address2", values.address2);
            formData.append("city", values.city);
            formData.append("state", values.state);
            formData.append("stateCode", values.stateCode || "");
            formData.append("zipCode", values.zip);

            // Handle profile image
            if (profileImage) {
                formData.append("profileImage", profileImage);
            }

            // Call API to update profile - using the same endpoint as Profile component
            const response = await axios.post(
                `${apiConfig.apiBaseURL}/userProfileCreation`,
                formData,
                {
                    headers: {
                        "x-api-key": apiConfig["x-api-key"],
                        "Content-Type": "multipart/form-data"
                    }
                }
            );

            if (response.data && response.data.success) {
                toast.success("Profile updated successfully!");
                window.dispatchEvent(new Event('auth-updated'));
                setTimeout(() => {
                    navigate("/profile");
                }, 2000);
            } else {
                toast.error("Failed to update profile. Please try again.");
            }
        } catch (error) {
            console.error("Error updating profile:", error);
            const errorMessage = error.response?.data?.message || "Error updating profile. Please try again later.";
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <Loader/>
    }

    const getInitials = () => {
        const first = formData.firstName?.charAt(0) || '';
        const last = formData.lastName?.charAt(0) || '';
        return (first + last).toUpperCase();
    };

    return (
        <div className="container my-4 credential-page edit-profile bg-white py-5">
            <Container>
                <Row className="justify-content-center">
                    <h5 className="fw-bolder text-center">Edit Profile</h5>
                    <Col md={12} lg={8}>
                        <div className="p-4 position-relative">
                            <div className="profile-image-container text-center mb-3 d-flex justify-content-center">
                                {/* <Image
                                    src={previewImage}
                                    roundedCircle
                                    width={150}
                                    height={150}
                                    className="profile-image"
                                    style={{ border: '3px solid #FFFFFF', objectFit: 'cover' }}
                                    onError={(e) => {
                                        console.log("Image failed to load:", e.target.src);
                                        e.target.onerror = null;
                                        e.target.src = ProfileImage;
                                    }}
                                /> */}

                                {previewImage ? (
                                    <Image
                                        src={previewImage}
                                        roundedCircle
                                        width={150}
                                        height={150}
                                        className="profile-image"
                                        style={{ border: '3px solid #FFFFFF', objectFit: 'cover' }}
                                        onError={(e) => {
                                            console.log("Image failed to load:", e.target.src);
                                            e.target.onerror = null;
                                            setPreviewImage(null);  // fallback to initials if image fails
                                        }}
                                    />
                                ) : (
                                    <div
                                        className="d-flex align-items-center justify-content-center bg-secondary text-white rounded-circle profile-image"
                                        style={{
                                            width: "150px",
                                            height: "150px",
                                            border: '3px solid #FFFFFF',
                                            fontSize: '3rem'
                                        }}
                                    >
                                        {getInitials()}
                                    </div>
                                )}
                                <label className="edit-icon position-absolute bg-primary rounded-circle" style={{ bottom: '17%', right: '41%', width: "50px", height: "50px", border: "3px solid #FFFFFF", cursor: "pointer" }}>
                                    <img src={IocnCamera} alt='click' style={{ paddingTop: '9px' }} />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="d-none"
                                        onChange={handleImageChange}
                                    />
                                </label>
                            </div>
                        </div>

                        {statesError && (
                            <div className="alert alert-danger" role="alert">
                                {statesError}
                            </div>
                        )}

                        <Formik
                            initialValues={formData}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                            enableReinitialize={true}
                        >
                            {({ values, setFieldValue }) => (
                                <FormikForm>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="formBasicEmail">
                                                <Form.Label className="mb-1">First Name*</Form.Label>
                                                <Field name="firstName" className="form-control p-3" />
                                                <ErrorMessage name="firstName" component="div" className="text-danger" />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="formBasicEmail">
                                                <Form.Label className="mb-1">Last Name*</Form.Label>
                                                <Field name="lastName" className="form-control p-3" />
                                                <ErrorMessage name="lastName" component="div" className="text-danger" />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3 position-relative">
                                                <Form.Label className="mb-1">Email*</Form.Label>
                                                <Field
                                                    name="email"
                                                    className="form-control p-3"
                                                    disabled={isEmailVerified}
                                                    onChange={(e) => {
                                                        const newEmail = e.target.value;
                                                        setFieldValue("email", newEmail);
                                                        setTempEmail(newEmail);

                                                        // If the email is same as verified email, mark it as verified
                                                        if (newEmail === verifiedEmail) {
                                                            setIsEmailVerified(true);
                                                        } else {
                                                            setIsEmailVerified(false);
                                                        }
                                                    }}
                                                />

                                                <ErrorMessage name="email" component="div" className="text-danger position-relative" />
                                                {values.email &&
                                                    values.email !== verifiedEmail &&
                                                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email) && (
                                                        <Button variant="link"
                                                            className="position-absolute end-0 text-primary fw-bold"
                                                            style={{ fontSize: '12px', right: '10px', fontStyle: 'italic' }}
                                                            onClick={handleVerifyEmail}>
                                                            Verify Email
                                                        </Button>
                                                    )}
                                                {emailVerificationError && <div className="text-danger">Please verify your email.</div>}
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="formPhone">
                                                <Form.Label className="mb-1">Phone</Form.Label>
                                                <Field
                                                    name="phone"
                                                    className="form-control p-3"
                                                    onChange={(e) => {
                                                        let value = e.target.value;
                                                        // Remove all non-digit characters
                                                        let numericValue = value.replace(/\D/g, "");

                                                        // Limit to 10 digits
                                                        if (numericValue.length > 10) {
                                                            numericValue = numericValue.slice(0, 10);
                                                        }

                                                        // Format based on length
                                                        if (numericValue.length > 6) {
                                                            value = `(${numericValue.slice(0, 3)}) ${numericValue.slice(3, 6)}-${numericValue.slice(6, 10)}`;
                                                        } else if (numericValue.length > 3) {
                                                            value = `(${numericValue.slice(0, 3)}) ${numericValue.slice(3)}`;
                                                        } else if (numericValue.length > 0) {
                                                            value = `(${numericValue}`;
                                                        }

                                                        setFieldValue("phone", value);
                                                    }}
                                                />
                                                <ErrorMessage name="phone" component="div" className="text-danger" />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="formBasicEmail">
                                                <Form.Label className="mb-1">Company Name*</Form.Label>
                                                <Field name="companyName" className="form-control p-3" />
                                                <ErrorMessage name="companyName" component="div" className="text-danger" />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="formBasicEmail">
                                                <Form.Label className="mb-1">Title</Form.Label>
                                                <Field name="title" className="form-control p-3" />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="formBasicEmail">
                                                <Form.Label className="mb-1">Address 1*</Form.Label>
                                                <Field name="address1" className="form-control p-3" />
                                                <ErrorMessage name="address1" component="div" className="text-danger" />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="formBasicEmail">
                                                <Form.Label className="mb-1">Address 2</Form.Label>
                                                <Field name="address2" className="form-control p-3" />
                                            </Form.Group>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Group className="mb-3" controlId="formBasicEmail">
                                                <Form.Label className="mb-1">City*</Form.Label>
                                                <Field name="city" className="form-control p-3" />
                                                <ErrorMessage name="city" component="div" className="text-danger" />
                                            </Form.Group>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Group className="mb-3" controlId="formState">
                                                <Form.Label className="mb-1">State*</Form.Label>
                                                <Form.Select
                                                    name="state"
                                                    onChange={(e) => {
                                                        // Find the selected state object
                                                        const selectedState = states.find(
                                                            state => state.code === e.target.value
                                                        );
                                                        setFieldValue("stateCode", e.target.value);
                                                        setFieldValue("state", selectedState ? selectedState.name : "");
                                                    }}
                                                    value={values.stateCode || ""}
                                                    className="form-control p-3"
                                                    disabled={statesLoading}
                                                >
                                                    <option value="">Select State</option>
                                                    {statesLoading ? (
                                                        <option>Loading states...</option>
                                                    ) : (
                                                        states.map(state => (
                                                            <option key={state._id} value={state.code}>
                                                                {state.name}
                                                            </option>
                                                        ))
                                                    )}
                                                </Form.Select>
                                                <ErrorMessage name="state" component="div" className="text-danger" />
                                            </Form.Group>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Group className="mb-3" controlId="formBasicEmail">
                                                <Form.Label className="mb-1">Zip*</Form.Label>
                                                <Field name="zip" className="form-control p-3" />
                                                <ErrorMessage name="zip" component="div" className="text-danger" />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <div className="d-flex justify-content-end my-3">
                                        <Button className="ms-2 px-5 py-3" onClick={handleCancel} variant="outline-primary">
                                            Cancel
                                        </Button>

                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="px-5 py-3 ms-2"
                                            disabled={!isEmailVerified || isSubmitting}
                                        >
                                            {isSubmitting ? "Updating..." : "Update"}
                                        </Button>
                                    </div>
                                </FormikForm>
                            )}
                        </Formik>
                    </Col>
                </Row>
            </Container>

            {/* <ToastContainer position="top-right" autoClose={3000} hideProgressBar /> */}

            <Modal show={showOtpModal} backdrop="static" centered onHide={() => {
                setShowOtpModal(false);
                setEmailVerificationError(true);
            }}>
                <Modal.Header closeButton>
                    <Modal.Title>Verify OTP</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Please enter the OTP sent to {tempEmail}:</p>
                    <div className="d-flex justify-content-center">
                        <OtpInput
                            value={otp}
                            onChange={handleOtpChange}
                            numInputs={5}
                            separator={<span style={{ width: "8px" }}></span>}
                            renderSeparator={<span>&nbsp;&nbsp;&nbsp;</span>}
                            isInputNum={true}
                            shouldAutoFocus={true}
                            inputStyle={{
                                background: "#E7EFF8",
                                border: "0",
                                borderRadius: "8px",
                                height: "51px",
                                width: "70px",
                                fontSize: "20px",
                                color: "#677289",
                                fontWeight: "700",
                                caretColor: "blue",
                                textAlign: "center",
                            }}
                            focusStyle={{
                                border: "none",
                                outline: "none",
                            }}
                            renderInput={(props) => <input {...props} />}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setShowOtpModal(false);
                        setEmailVerificationError(true);
                    }}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleOtpSubmit}>
                        Verify
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default EditProfile;